/**
 * Intent detector for scroll-driven slide transitions.
 *
 * Takes a stream of wheel events and classifies each as a continuation of
 * the current gesture or the start of a new one. When a new gesture is
 * detected the result carries a `fire` direction; the caller decides what
 * to do with it (start a transition, queue, ignore, etc).
 *
 * The detector is pure — no DOM, no timers, no `performance.now()`. The
 * caller supplies a timestamp with every event, which makes tests
 * deterministic: replay a captured event stream and assert the output.
 *
 * Gesture model:
 *   - One `fire` per physical flick. Subsequent events in the same
 *     gesture's momentum tail are absorbed as `continue`.
 *   - A new fire needs one of: silent gap (`fresh`), discrete wheel click
 *     (`discrete`), meaningful direction reversal (`reversal`), or a
 *     confirmed delta spike above a decayed tail (`pending` → `confirm`).
 *   - macOS momentum tails produce occasional lone spikes that exceed the
 *     gesture's peak, so spikes alone never fire — the next event has to
 *     confirm that input is sustained. A lone spike with no follow-up is
 *     absorbed as noise.
 */

export interface WheelLike {
  deltaY: number;
  deltaMode: number;
  timestamp: number;
}

export type Classification =
  | "skip-tiny"
  | "skip-gap"
  | "fresh"
  | "discrete"
  | "reversal"
  | "reflick"
  | "pending"
  | "confirm"
  | "dismiss"
  | "stale-dismiss"
  | "continue";

export interface Result {
  classification: Classification;
  fire?: 1 | -1;
}

export interface LogContext {
  dt: number;
  absDelta: number;
  dir: number;
  deltaMode: number;
  peak: number;
  tailMax: number;
  tail: readonly number[];
  pendingAbsDelta?: number;
}

export interface ScrollIntentOptions {
  onLog?: (classification: Classification, ctx: LogContext) => void;
}

interface PendingState {
  kind: "spike" | "fresh" | "reflick";
  dir: 1 | -1;
  absDelta: number;
  preTailMax: number;
  time: number;
}

function asAxisDir(value: number): 1 | -1 {
  return value < 0 ? -1 : 1;
}

export const INTENT_CONFIG = Object.freeze({
  /** Below this delta a wheel event is ignored entirely. */
  SKIP_TINY_DELTA: 4,
  /** A small delta with a long gap is skipped without updating lastTime —
   * often the OS winding momentum down as the user touches the pad for a
   * second flick. Surfaces the real gap on the next meaningful event. */
  SKIP_GAP_DT_MS: 50,
  SKIP_GAP_MAX_DELTA: 10,
  /** Gap since last event beyond which the current event starts a new gesture. */
  FRESH_GAP_MS: 100,
  /** Minimum follow-up delta required to confirm a fresh-after-gap pending.
   * Late momentum spikes collapse to noise below this; real new flicks sustain. */
  FRESH_CONFIRM_MIN_DELTA: 8,
  /** Line/page mode or (long gap + big delta) identifies a discrete wheel click. */
  DISCRETE_GAP_MS: 50,
  DISCRETE_MIN_DELTA: 50,
  /** Direction reversal must carry at least this delta to count. */
  REVERSAL_MIN_DELTA: 15,
  /** Minimum delta for an event to enter pending. */
  PENDING_MIN_DELTA: 15,
  /** Delta must be this many times the recent tail max to be a spike. */
  PENDING_SPIKE_RATIO: 2.5,
  /** Gesture peak must exceed this ratio over tail max to qualify as "in decay".
   * Prevents ramp-up jumps from entering pending. */
  PENDING_DECAY_RATIO: 3,
  /** Continuous re-flick triggers when either the peak is exceeded by this
   * factor, or the delta clearly beats the recent tail max by REFLICK_ACCEL_RATIO
   * (below). The peak-ratio path catches flicks whose peak plainly beats the
   * prior one; the accel path catches flicks of similar strength that never
   * clear the peak-ratio. */
  REFLICK_PEAK_RATIO: 1.3,
  REFLICK_ACCEL_RATIO: 1.3,
  /** Recent tail min must be below this fraction of peak for the gesture to
   * count as "decayed" — i.e. not still ramping up. */
  REFLICK_DECAY_RATIO: 0.3,
  /** Absolute floor on the re-accelerating delta. Noise deltas late in a
   * gesture's tail can exceed 2× tail min (e.g. 10 > 2×4) without being a
   * real re-flick. */
  REFLICK_MIN_DELTA: 15,
  /** Time since the current peak was reached beyond which a new peak is
   * eligible as a re-flick. Guards against ramp-up phases where peak is
   * being set repeatedly. */
  REFLICK_MIN_PEAK_AGE_MS: 100,
  /** Pending older than this on the next event's arrival is dismissed
   * outright — the stream has effectively ended, don't chain into a new gesture. */
  PENDING_STALE_MS: 150,
  /** Next event confirms pending if its delta is at least this fraction of pending's. */
  CONFIRM_SUSTAINED_RATIO: 0.8,
  /** Alternative confirmation: delta exceeds pre-pending tail max by this ratio. */
  CONFIRM_ABOVE_DECAY_RATIO: 3,
  /** Rolling window size for recent deltas. */
  TAIL_WINDOW: 5,
  /** How many of the most recent deltas count toward tailMax. */
  TAIL_RECENT: 3,
});

export class ScrollIntent {
  #peak = 0;
  #peakTime = 0;
  #direction = 0;
  #lastTime = 0;
  #tail: number[] = [];
  #pending: PendingState | null = null;
  #onLog: ((c: Classification, ctx: LogContext) => void) | null;

  constructor(options: ScrollIntentOptions = {}) {
    this.#onLog = options.onLog ?? null;
  }

  reset(): void {
    this.#peak = 0;
    this.#peakTime = 0;
    this.#direction = 0;
    this.#lastTime = 0;
    this.#tail.length = 0;
    this.#pending = null;
  }

  /**
   * Prime the detector as if a gesture in `dir` were already underway.
   * Used when another input path (scroll-to-top reengagement) has already
   * fired a transition and subsequent wheel events from the same physical
   * flick should be absorbed rather than trigger another fire.
   */
  seed(dir: 1 | -1, now: number): void {
    this.reset();
    this.#direction = dir;
    this.#peak = 1;
    this.#peakTime = now;
    this.#lastTime = now;
    this.#tail.push(1);
  }

  /**
   * Classify a wheel event. May return a `fire` direction the caller
   * should act on.
   */
  process(event: WheelLike): Result {
    const absDelta = Math.abs(event.deltaY);
    const sign = Math.sign(event.deltaY);
    const dir: 1 | -1 | 0 = sign === 0 ? 0 : sign < 0 ? -1 : 1;
    const now = event.timestamp;

    if (absDelta < INTENT_CONFIG.SKIP_TINY_DELTA) {
      this.#log("skip-tiny", { dt: 0, absDelta, dir, deltaMode: event.deltaMode });
      return { classification: "skip-tiny" };
    }

    const dt = now - this.#lastTime;

    if (
      this.#peak > 0 &&
      dt > INTENT_CONFIG.SKIP_GAP_DT_MS &&
      absDelta < INTENT_CONFIG.SKIP_GAP_MAX_DELTA
    ) {
      this.#log("skip-gap", { dt, absDelta, dir, deltaMode: event.deltaMode });
      return { classification: "skip-gap" };
    }

    this.#lastTime = now;

    // Resolve or drop a pending spike before classifying the current event.
    if (this.#pending) {
      const pendingAge = now - this.#pending.time;
      if (pendingAge > INTENT_CONFIG.PENDING_STALE_MS) {
        this.#log("stale-dismiss", {
          dt,
          absDelta,
          dir,
          deltaMode: event.deltaMode,
          pendingAbsDelta: this.#pending.absDelta,
        });
        this.#absorbPending();
      } else {
        const confirmed = this.#resolvePending(dir, absDelta, event.deltaMode, dt);
        if (confirmed) return confirmed;
      }
    }

    const tailMax = this.#tailMax();

    if (this.#peak === 0) {
      this.#log("fresh", { dt, absDelta, dir, deltaMode: event.deltaMode, tailMax });
      const dirAxis = asAxisDir(dir);
      this.#startGesture(absDelta, dirAxis, now);
      return { classification: "fresh", fire: dirAxis };
    }

    // A meaningful gap after an active gesture looks like a new flick, but a
    // lone momentum spike arriving after the gesture's tail has collapsed can
    // produce the same shape. Defer to pending so the next event decides.
    if (dt > INTENT_CONFIG.FRESH_GAP_MS) {
      this.#pending = {
        kind: "fresh",
        dir: asAxisDir(dir),
        absDelta,
        preTailMax: tailMax,
        time: now,
      };
      this.#log("pending", { dt, absDelta, dir, deltaMode: event.deltaMode, tailMax });
      return { classification: "pending" };
    }

    if (
      event.deltaMode !== 0 ||
      (dt > INTENT_CONFIG.DISCRETE_GAP_MS &&
        absDelta > INTENT_CONFIG.DISCRETE_MIN_DELTA &&
        absDelta > this.#peak)
    ) {
      this.#log("discrete", { dt, absDelta, dir, deltaMode: event.deltaMode, tailMax });
      const dirAxis = asAxisDir(dir);
      this.#startGesture(absDelta, dirAxis, now);
      return { classification: "discrete", fire: dirAxis };
    }

    if (dir !== this.#direction && absDelta > INTENT_CONFIG.REVERSAL_MIN_DELTA) {
      this.#log("reversal", { dt, absDelta, dir, deltaMode: event.deltaMode, tailMax });
      const dirAxis = asAxisDir(dir);
      this.#startGesture(absDelta, dirAxis, now);
      return { classification: "reversal", fire: dirAxis };
    }

    // Continuous re-flick: same-direction re-acceleration after the gesture
    // has decayed. The peak-age guard rules out ramp-up phases.
    const tailMin = this.#tailMin();
    const newPeak = absDelta > this.#peak * INTENT_CONFIG.REFLICK_PEAK_RATIO;
    const reAccel =
      tailMax > 0 &&
      absDelta >= INTENT_CONFIG.REFLICK_MIN_DELTA &&
      absDelta > tailMax * INTENT_CONFIG.REFLICK_ACCEL_RATIO;
    const decayed = tailMin < this.#peak * INTENT_CONFIG.REFLICK_DECAY_RATIO;
    const peakAged = now - this.#peakTime > INTENT_CONFIG.REFLICK_MIN_PEAK_AGE_MS;
    if (dir === this.#direction && decayed && peakAged) {
      // A new peak well above the prior is a strong enough signal to fire
      // immediately — typical of a second flick that physically beats the
      // first.
      if (newPeak) {
        this.#log("reflick", { dt, absDelta, dir, deltaMode: event.deltaMode, tailMax });
        const dirAxis = asAxisDir(dir);
        this.#startGesture(absDelta, dirAxis, now);
        return { classification: "reflick", fire: dirAxis };
      }
      // Re-acceleration without a new peak is ambiguous — a lone momentum
      // spike can look like one. Defer to pending so the next event decides.
      if (reAccel) {
        this.#pending = {
          kind: "reflick",
          dir: asAxisDir(dir),
          absDelta,
          preTailMax: tailMax,
          time: now,
        };
        this.#log("pending", { dt, absDelta, dir, deltaMode: event.deltaMode, tailMax });
        return { classification: "pending" };
      }
    }

    if (
      dir === this.#direction &&
      absDelta > INTENT_CONFIG.PENDING_MIN_DELTA &&
      tailMax > 0 &&
      absDelta > tailMax * INTENT_CONFIG.PENDING_SPIKE_RATIO &&
      this.#peak > tailMax * INTENT_CONFIG.PENDING_DECAY_RATIO
    ) {
      this.#pending = {
        kind: "spike",
        dir: asAxisDir(dir),
        absDelta,
        preTailMax: tailMax,
        time: now,
      };
      this.#log("pending", { dt, absDelta, dir, deltaMode: event.deltaMode, tailMax });
      return { classification: "pending" };
    }

    this.#log("continue", { dt, absDelta, dir, deltaMode: event.deltaMode, tailMax });
    this.#updatePeak(absDelta, now);
    this.#pushTail(absDelta);
    return { classification: "continue" };
  }

  #resolvePending(dir: number, absDelta: number, deltaMode: number, dt: number): Result | null {
    const p = this.#pending;
    if (!p) return null;
    this.#pending = null;

    const sameDir = dir === p.dir;
    let confirmed: boolean;
    if (p.kind === "fresh") {
      // A new flick sustains meaningful input; a late momentum spike collapses
      // to near-noise. Any follow-up above the noise floor counts.
      confirmed = sameDir && absDelta >= INTENT_CONFIG.FRESH_CONFIRM_MIN_DELTA;
    } else if (p.kind === "reflick") {
      // A real re-flick keeps climbing after the pending event; a momentum
      // oscillation falls back. Confirm only if the follow-up at least holds
      // the pending level.
      confirmed = sameDir && absDelta >= p.absDelta;
    } else {
      const sustained = absDelta >= p.absDelta * INTENT_CONFIG.CONFIRM_SUSTAINED_RATIO;
      const aboveDecay = absDelta > p.preTailMax * INTENT_CONFIG.CONFIRM_ABOVE_DECAY_RATIO;
      confirmed = sameDir && (sustained || aboveDecay);
    }

    if (confirmed) {
      this.#log("confirm", {
        dt,
        absDelta,
        dir,
        deltaMode,
        tailMax: this.#tailMax(),
        pendingAbsDelta: p.absDelta,
      });
      this.#startGesture(p.absDelta, p.dir, p.time);
      this.#updatePeak(absDelta, this.#lastTime);
      this.#pushTail(absDelta);
      return { classification: "confirm", fire: p.dir };
    }

    this.#log("dismiss", {
      dt,
      absDelta,
      dir,
      deltaMode,
      tailMax: this.#tailMax(),
      pendingAbsDelta: p.absDelta,
    });
    this.#updatePeak(p.absDelta, p.time);
    // A dismissed spike/reflick was momentum noise that happened to exceed
    // the tail briefly; pushing it would poison tailMax and keep subsequent
    // real re-accelerations from clearing PENDING_SPIKE_RATIO / REFLICK_ACCEL_RATIO.
    // Fresh-gap dismissals are different: they're part of an ongoing gesture's
    // observed stream, so they belong in the tail.
    if (p.kind === "fresh") this.#pushTail(p.absDelta);
    return null;
  }

  #absorbPending(): void {
    const p = this.#pending;
    if (!p) return;
    this.#pending = null;
    this.#updatePeak(p.absDelta, p.time);
    this.#pushTail(p.absDelta);
  }

  #startGesture(absDelta: number, dir: 1 | -1, now: number): void {
    this.#peak = absDelta;
    this.#peakTime = now;
    this.#direction = dir;
    this.#tail.length = 0;
    this.#tail.push(absDelta);
  }

  #updatePeak(absDelta: number, now: number): void {
    if (absDelta > this.#peak) {
      this.#peak = absDelta;
      this.#peakTime = now;
    }
  }

  #pushTail(absDelta: number): void {
    this.#tail.push(absDelta);
    if (this.#tail.length > INTENT_CONFIG.TAIL_WINDOW) this.#tail.shift();
  }

  #tailMax(): number {
    const recent = this.#tail.slice(-INTENT_CONFIG.TAIL_RECENT);
    return recent.length > 0 ? Math.max(...recent) : 0;
  }

  #tailMin(): number {
    const recent = this.#tail.slice(-INTENT_CONFIG.TAIL_RECENT);
    return recent.length > 0 ? Math.min(...recent) : 0;
  }

  #log(
    classification: Classification,
    partial: Omit<LogContext, "peak" | "tail" | "tailMax"> & { tailMax?: number },
  ): void {
    if (!this.#onLog) return;
    this.#onLog(classification, {
      dt: partial.dt,
      absDelta: partial.absDelta,
      dir: partial.dir,
      deltaMode: partial.deltaMode,
      peak: this.#peak,
      tailMax: partial.tailMax ?? this.#tailMax(),
      tail: [...this.#tail],
      pendingAbsDelta: partial.pendingAbsDelta,
    });
  }
}
